<template>
  <div class="relative min-h-[52px] flex flex-col gap-3 w-full">
    <!-- Single Deposit Button (collapsed state) -->
    <transition name="fade-slide">
      <button
        v-if="!isExpanded"
        type="button"
        class="btn-primary w-full flex items-center justify-center gap-2"
        :disabled="loading"
        @click="handleExpand"
      >
        <CirclePlus :size="18" />
        {{ $t('walletSummary.deposit') }}
      </button>
    </transition>

    <!-- Expanded State: Two Options -->
    <transition name="split">
      <div v-if="isExpanded" class="flex flex-col gap-3 w-full deposit-options">
        <!-- Buy with Crypto Option -->
        <button
          type="button"
          class="btn-option crypto-option"
          :disabled="loading"
          @click="handleSelectMethod('crypto')"
        >
          <div class="option-icon crypto-icon">
            <ArrowLeftRight :size="20" />
          </div>
          <div class="flex-1 flex flex-col items-start gap-1 text-left">
            <span class="text-base font-semibold text-vault-text-primary leading-tight">{{ $t('depositChoice.crypto.title') }}</span>
            <span class="text-xs font-normal text-vault-text-secondary leading-snug">{{ $t('depositChoice.crypto.description') }}</span>
          </div>
        </button>

        <!-- Buy with Card Option -->
        <button
          type="button"
          class="btn-option card-option"
          :disabled="loading"
          @click="handleSelectMethod('card')"
        >
          <div class="option-icon card-icon">
            <CreditCard :size="20" />
          </div>
          <div class="flex-1 flex flex-col items-start gap-1 text-left">
            <span class="text-base font-semibold text-vault-text-primary leading-tight">{{ $t('depositChoice.card.title') }}</span>
            <span class="text-xs font-normal text-vault-text-secondary leading-snug">{{ $t('depositChoice.card.description') }}</span>
          </div>
        </button>

        <!-- Back/Cancel Button (hidden when always expanded) -->
        <button
          v-if="!alwaysExpanded"
          type="button"
          class="btn-cancel"
          :disabled="loading"
          @click="handleCollapse"
        >
          <X :size="16" />
          {{ $t('common.cancel') }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CirclePlus, ArrowLeftRight, CreditCard, X } from 'lucide-vue-next'

const { t: $t } = useI18n()

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  alwaysExpanded: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select-method'])

const isExpanded = ref(props.alwaysExpanded)

function handleExpand() {
  if (props.loading) return
  isExpanded.value = true
}

function handleCollapse() {
  if (props.loading) return
  isExpanded.value = false
}

function handleSelectMethod(method) {
  if (props.loading) return
  emit('select-method', method)
  // Keep expanded state so parent can handle navigation
}
</script>

<style scoped>
/* Option Buttons */
.btn-option {
  @apply flex items-center justify-start gap-3.5 px-5 py-4 bg-white/95 border-2 border-vault-blue-400/20 rounded-xl cursor-pointer transition-all duration-300 w-full shadow-sm;
}

.btn-option:hover:not(:disabled) {
  transform: translateX(4px);
  @apply border-vault-blue-400/40 shadow-md;
}

.btn-option:active:not(:disabled) {
  transform: translateX(2px);
}

.btn-option:disabled {
  @apply opacity-50 cursor-not-allowed;
}

/* Crypto Option Specific */
.crypto-option:hover:not(:disabled) {
  background: rgba(102, 126, 234, 0.05);
}

/* Card Option Specific */
.card-option:hover:not(:disabled) {
  background: rgba(67, 160, 71, 0.05);
}

/* Option Icon */
.option-icon {
  @apply flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-200;
}

.btn-option:hover .option-icon {
  transform: scale(1.1);
}

.crypto-icon {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
  color: #667eea;
}

.card-icon {
  background: linear-gradient(135deg, rgba(67, 160, 71, 0.15), rgba(46, 125, 50, 0.15));
  color: #43a047;
}

/* Cancel Button */
.btn-cancel {
  @apply flex items-center justify-center gap-1.5 px-4 py-2.5 bg-transparent border border-vault-blue-400/20 text-vault-blue-400 text-sm font-semibold rounded-xl cursor-pointer transition-all duration-300 w-full;
}

.btn-cancel:hover:not(:disabled) {
  background-color: rgba(0, 123, 255, 0.08);
  border-color: rgba(0, 123, 255, 0.3);
}

.btn-cancel:disabled {
  @apply opacity-50 cursor-not-allowed;
}

/* Animations */

/* Fade and slide out for single button */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

/* Split animation for expanded state */
.split-enter-active {
  animation: splitIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.split-leave-active {
  animation: splitOut 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes splitIn {
  0% {
    opacity: 0;
    transform: scaleY(0.5);
    transform-origin: center top;
  }
  40% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
    transform: scaleY(1);
  }
}

@keyframes splitOut {
  0% {
    opacity: 1;
    transform: scaleY(1);
    transform-origin: center top;
  }
  100% {
    opacity: 0;
    transform: scaleY(0.5);
  }
}

/* Staggered entrance for buttons */
.deposit-options .btn-option:nth-child(1) {
  animation: slideInLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s backwards;
}

.deposit-options .btn-option:nth-child(2) {
  animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s backwards;
}

.deposit-options .btn-cancel {
  animation: fadeIn 0.3s ease 0.3s backwards;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Mobile responsive */
@media (max-width: 480px) {
  .btn-option {
    padding: 14px 16px;
    gap: 12px;
  }

  .option-icon {
    width: 40px;
    height: 40px;
  }

  .option-title {
    font-size: 14px;
  }

  .option-subtitle {
    font-size: 11px;
  }
}
</style>
